import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { FileDownIcon, FileImageIcon, ListChecksIcon, PresentationIcon, PlusIcon, Settings2Icon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Field, FieldDescription, FieldLabel } from '../../../components/ui/field';
import { Input } from '../../../components/ui/input';
import { Spinner } from '../../../components/ui/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { readRemembered, useRemembered, writeRemembered } from '../../../shared/lib/remembered';
import { ExportMenu, type ExportFormat } from '../../statistics/components/ExportMenu';
import { extractShortcode } from '../../statistics/lib/parse-instagram-url';
import { extractLinks } from '../lib/extract-links';
import type { ArticleSlide } from '../lib/presentation-input';
import { MONTHS_RU, pickReportPeriod } from '../lib/report-period';
import { generatePresentationPdf, preloadPresentationAssets } from '../lib/render/generate-pdf';
import { generatePresentationPptx } from '../lib/render/generate-pptx';
import { generatePresentationSvg } from '../lib/render/generate-svg';
import { useContactTemplates } from '../model/useContactTemplates';
import { useLogoTemplates } from '../model/useLogoTemplates';
import { slidesOf, usePresentationPosts } from '../model/usePresentationPosts';
import { FoundPostCard, ManualPostCard } from './post-cards';
import { ContactTemplatesDialog } from './contact-templates-dialog';
import { ImageField } from './image-field';
import { LogoTemplatesDialog } from './logo-templates-dialog';

type ArticleDraft = {
  key: number;
  file: File | null;
  domain: string;
  url: string;
};

/** Во что собираем отчёт. Макет один, отличается только файл на выходе. */
const FORMATS = {
  pdf: {
    label: 'PDF',
    hint: 'для клиента',
    build: generatePresentationPdf,
    ext: 'pdf',
  },
  pptx: {
    label: 'PowerPoint',
    hint: 'дособрать и переставить слайды',
    build: generatePresentationPptx,
    ext: 'pptx',
  },
  svg: {
    label: 'SVG для Figma',
    hint: 'править макет слоями',
    build: generatePresentationSvg,
    ext: 'svg',
  },
} as const;

type FormatKey = keyof typeof FORMATS;

/** Формат помнится между заходами: человек обычно сдаёт отчёты в одном. */
const FORMAT_KEY = 'traction.reports.format';

function lastFormat(): FormatKey {
  try {
    const saved = localStorage.getItem(FORMAT_KEY);
    if (saved && saved in FORMATS) return saved as FormatKey;
  } catch {
    // Приватное окно или запрет на хранилище — просто берём формат по умолчанию.
  }
  return 'pdf';
}

/** Формат теперь помнит общая кнопка выгрузки; прежний выбор переносим один раз. */
const EXPORT_KEY = 'report:presentation';
function migrateFormat(): void {
  if (readRemembered<string | null>(`export:${EXPORT_KEY}`, null) === null) {
    writeRemembered(`export:${EXPORT_KEY}`, lastFormat());
  }
}

const FORMAT_ICONS: Record<FormatKey, ExportFormat['icon']> = {
  pdf: FileDownIcon,
  pptx: PresentationIcon,
  svg: FileImageIcon,
};
const EXPORT_FORMATS: ExportFormat[] = (Object.keys(FORMATS) as FormatKey[]).map((key) => ({
  id: key,
  label: FORMATS[key].label,
  description: FORMATS[key].hint[0].toUpperCase() + FORMATS[key].hint.slice(1),
  icon: FORMAT_ICONS[key],
}));

const AUTO = 'auto';
const NO_CONTACT = 'none';
const NO_LOGO = 'none';
/** Логотип не из шаблонов, а файлом с компьютера. */
const LOGO_FILE = 'file';

function domainFromUrl(url: string): string {
  try {
    return new URL(url.trim()).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function download(blob: Blob, filename: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(link.href);
}

/**
 * Заголовок группы полей: слово, линия до края и место под кнопку.
 *
 * Форма длинная, и без таких перемычек она читается как один сплошной
 * список. Заголовок короткий — объяснения полей живут в самих полях.
 */
function SectionTitle({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="flex items-center gap-3">
      <span className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
        {children}
      </span>
      <span className="bg-border h-px flex-1" />
      {action}
    </div>
  );
}

/** Кнопка «Шаблоны» — значком: подпись рядом с полем только мешает. */
function TemplatesButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <Button
      variant="outline"
      size="icon"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
    >
      <Settings2Icon />
    </Button>
  );
}

function ArticleRow({
  article,
  index,
  onChange,
  onRemove,
  disabled,
}: {
  article: ArticleDraft;
  index: number;
  onChange: (patch: Partial<ArticleDraft>) => void;
  onRemove: () => void;
  disabled: boolean;
}): React.ReactElement {
  return (
    <div className="space-y-3 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">Статья {index + 1}</p>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Убрать статью"
          onClick={onRemove}
          disabled={disabled}
        >
          <Trash2Icon />
        </Button>
      </div>
      <ImageField
        id={`article-shot-${article.key}`}
        label="Скриншот страницы"
        hint="В экран ноутбука на слайде"
        file={article.file}
        onChange={(file) => onChange({ file })}
        disabled={disabled}
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <FieldLabel htmlFor={`article-domain-${article.key}`}>Домен</FieldLabel>
          <Input
            id={`article-domain-${article.key}`}
            placeholder="turaninfo.kz"
            value={article.domain}
            onChange={(e) => onChange({ domain: e.target.value })}
            disabled={disabled}
          />
        </div>
        <div className="space-y-1">
          <FieldLabel htmlFor={`article-url-${article.key}`}>Ссылка</FieldLabel>
          <Input
            id={`article-url-${article.key}`}
            placeholder="https://turaninfo.kz/…"
            value={article.url}
            onChange={(e) => {
              const url = e.target.value;
              // Домен подставляется из ссылки, пока его не вписали руками.
              onChange(article.domain ? { url } : { url, domain: domainFromUrl(url) });
            }}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * «Презентация»: ссылки на публикации → готовый файл по макету «ШАБЛОНЫ ОТЧЕТА».
 * Цифры и превью приходят с сервера; статьи и контакты задаются здесь.
 */
export function PresentationForm(): React.ReactElement {
  const [linksText, setLinksText] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [cover, setCover] = useState<File | null>(null);
  const [month, setMonth] = useState<string>(AUTO);
  const [year, setYear] = useState('');
  const [articles, setArticles] = useState<ArticleDraft[]>([]);
  const [nextKey, setNextKey] = useState(1);
  // Логотип и контакт помнятся, как и формат: отчёты одному клиенту идут подряд.
  const [contactId, setContactId] = useRemembered<string>('report:presentation:contact', NO_CONTACT);
  const [contactsOpen, setContactsOpen] = useState(false);
  const [rememberedLogoId, setRememberedLogoId] = useRemembered<string>('report:presentation:logo', NO_LOGO);
  // Файл с компьютера после перезагрузки не вернуть — его не запоминаем.
  const [logoId, setLogoIdState] = useState<string>(rememberedLogoId);
  const setLogoId = (id: string) => {
    setLogoIdState(id);
    setRememberedLogoId(id === LOGO_FILE ? NO_LOGO : id);
  };
  const [logosOpen, setLogosOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  // До первой отрисовки кнопки выгрузки: она читает формат при создании.
  useState(migrateFormat);
  // Текст ссылок, по которому собраны публикации: правка ссылок после сбора
  // иначе молча уходила в файл старым набором.
  const [collectedText, setCollectedText] = useState<string | null>(null);

  const posts = usePresentationPosts();
  const templates = useContactTemplates();
  const logoTemplates = useLogoTemplates();

  // Запомненный шаблон удалили — не держим «Логотип не найден».
  useEffect(() => {
    if (logoTemplates.isLoading || logoTemplates.error || logoId === NO_LOGO || logoId === LOGO_FILE) return;
    if (logoTemplates.logos.some((l) => l.id === logoId)) return;
    setLogoIdState(NO_LOGO);
    setRememberedLogoId(NO_LOGO);
  }, [logoTemplates.isLoading, logoTemplates.error, logoTemplates.logos, logoId, setRememberedLogoId]);
  useEffect(() => {
    if (templates.isLoading || templates.error || contactId === NO_CONTACT) return;
    if (!templates.contacts.some((c) => c.id === contactId)) setContactId(NO_CONTACT);
  }, [templates.isLoading, templates.error, templates.contacts, contactId, setContactId]);

  const collectPosts = (text: string) => {
    setCollectedText(text);
    return posts.collect(text);
  };

  const links = useMemo(() => extractLinks(linksText), [linksText]);
  const validLinks = useMemo(() => links.filter((l) => extractShortcode(l)), [links]);

  const selectedLogo = logoTemplates.logos.find((l) => l.id === logoId) ?? null;
  const selectedContact = templates.contacts.find((c) => c.id === contactId) ?? null;
  /*
   * Подписи выбранного пункта — руками.
   *
   * Base UI рисует в кнопке значение, а не тот пункт списка, который ему
   * соответствует: без подписи в поле «Логотип» оказывался id шаблона,
   * а в «Периоде» — слово auto.
   */
  const logoLabel =
    logoId === NO_LOGO
      ? 'Без логотипа'
      : logoId === LOGO_FILE
        ? 'Файл с компьютера…'
        : (selectedLogo?.name ?? (logoTemplates.isLoading ? 'Загрузка…' : 'Логотип не найден'));
  const contactLabel =
    contactId === NO_CONTACT
      ? 'Без контактов'
      : selectedContact
        ? `${selectedContact.name}${selectedContact.role ? ` — ${selectedContact.role}` : ''}`
        : templates.isLoading
          ? 'Загрузка…'
          : 'Контакт не найден';

  /*
   * Месяц и год в форме показываем уже вычисленными, а не словом «авто»:
   * отчёт сдают за месяц кампании, и человеку нужно видеть, какой месяц
   * встанет на титул, а не обещание его посчитать.
   */
  const autoPeriod = useMemo(
    () =>
      pickReportPeriod(
        posts.slides.map((item) => item.slide.publishedAt),
        new Date(),
      ),
    [posts.slides],
  );
  const monthLabel = MONTHS_RU[month === AUTO ? autoPeriod.month : Number(month)];
  const periodIsAuto = month === AUTO || !year.trim();

  const yearNumber = year.trim() ? Number.parseInt(year, 10) : null;
  const yearValid = yearNumber === null || (yearNumber >= 2000 && yearNumber <= 2100);
  const articlesValid = articles.every((a) => a.file && a.domain.trim());
  const busy = isGenerating || posts.isCollecting;
  const linksChanged =
    posts.items.length > 0 && collectedText !== null && linksText.trim() !== collectedText.trim();
  const canGenerate =
    !busy &&
    (validLinks.length > 0 || posts.items.length > 0 || articles.length > 0) &&
    yearValid &&
    articlesValid;

  function patchArticle(key: number, patch: Partial<ArticleDraft>): void {
    setArticles((prev) => prev.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  }

  async function handleGenerate(format: FormatKey): Promise<void> {
    setIsGenerating(true);
    preloadPresentationAssets();
    try {
      // Обычный случай — вставили ссылки и сразу жмут «Сформировать»: тогда
      // публикации собираются здесь же. Если по части ссылок статистики нет,
      // показываем карточки и ждём, пока их заполнят.
      // Ссылки поправили после сбора — пересобираем: вписанное руками в
      // карточках без статистики при этом сохраняется (см. usePresentationPosts).
      let list = posts.slides;
      if ((posts.items.length === 0 || linksChanged) && validLinks.length > 0) {
        const collected = await collectPosts(linksText);
        list = slidesOf(collected);
        if (collected.some((item) => item.kind === 'manual')) {
          toast.info('По части публикаций статистики нет — заполните карточки ниже');
          return;
        }
      }

      if (list.length === 0 && articles.length === 0) {
        toast.error('Нет ни одной публикации для слайдов');
        return;
      }

      const auto = pickReportPeriod(
        list.map((item) => item.slide.publishedAt),
        new Date(),
      );
      const period = {
        year: yearNumber ?? auto.year,
        month: month === AUTO ? auto.month : Number(month),
      };

      // Шаблон — ссылка на картинку в Storage, свой логотип — файл.
      const logoSource = logoId === LOGO_FILE ? logo : (selectedLogo?.imageUrl ?? null);
      const articleSlides: ArticleSlide[] = articles.map((a) => ({
        screenshot: a.file as File,
        domain: a.domain.trim(),
        url: a.url.trim(),
      }));
      const first = list.find((item) => item.photo || item.slide.imageUrl);

      const chosen = FORMATS[format];
      const file = await chosen.build(
        {
          logo: logoSource,
          period,
          coverImage: cover ?? first?.photo ?? first?.slide.imageUrl ?? null,
          posts: list.map((item) => ({ ...item.slide, imageFile: item.photo })),
          articles: articleSlides,
          contact: selectedContact,
        },
        setProgress,
      );
      download(file, `Отчет ${MONTHS_RU[period.month]} ${period.year}.${chosen.ext}`);
      toast.success('Презентация готова');
    } catch (err) {
      console.error('[Presentation] сбой:', err);
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать презентацию');
    } finally {
      setIsGenerating(false);
      setProgress('');
    }
  }

  return (
    <div className="space-y-6">
      <Field>
        <FieldLabel htmlFor="presentation-links">Ссылки на публикации</FieldLabel>
        <Textarea
          id="presentation-links"
          rows={5}
          placeholder={'https://www.instagram.com/p/…\nhttps://www.instagram.com/reel/…'}
          value={linksText}
          onChange={(e) => setLinksText(e.target.value)}
          disabled={isGenerating}
        />
        <div className="flex items-center gap-3">
          <FieldDescription className="flex-1">
            {links.length === 0
              ? 'По одной в строке, в порядке слайдов'
              : `Ссылок: ${validLinks.length}${
                  links.length > validLinks.length
                    ? `, пропустим: ${links.length - validLinks.length}`
                    : ''
                }`}
          </FieldDescription>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => void collectPosts(linksText)}
            disabled={busy || validLinks.length === 0}
          >
            {posts.isCollecting ? <Spinner /> : <ListChecksIcon />}
            {linksChanged ? 'Пересобрать' : 'Собрать публикации'}
          </Button>
        </div>
        {linksChanged && (
          <FieldDescription className="text-amber-700 dark:text-amber-400">
            Ссылки изменились после сбора. «Сформировать» сначала пересоберёт публикации — вписанное
            в карточках вручную сохранится.
          </FieldDescription>
        )}
      </Field>

      {posts.items.length > 0 && (
        <div className="space-y-3">
          <SectionTitle
            action={
              posts.manualCount > 0 ? (
                <Badge variant="outline">Без статистики: {posts.manualCount}</Badge>
              ) : null
            }
          >
            Публикации · {posts.items.length}
          </SectionTitle>
          {posts.items.map((item, index) =>
            item.kind === 'found' ? (
              <FoundPostCard
                key={item.shortcode}
                slide={item.slide}
                onRemove={() => posts.remove(item.shortcode)}
                disabled={busy}
              />
            ) : (
              <ManualPostCard
                key={item.shortcode}
                post={item.post}
                index={index}
                onPatch={(patch) => posts.patchManual(item.shortcode, patch)}
                onScreen={(file) => void posts.readScreen(item.shortcode, file)}
                onRemove={() => posts.remove(item.shortcode)}
                disabled={busy}
              />
            ),
          )}
        </div>
      )}

      <div className="space-y-4">
        <SectionTitle>Оформление</SectionTitle>

        <div className="grid gap-4 md:grid-cols-2">
          <Field>
            <FieldLabel>Логотип</FieldLabel>
            <div className="flex gap-2">
              <Select
                value={logoId}
                onValueChange={(value) => setLogoId(value ?? NO_LOGO)}
                disabled={isGenerating || logoTemplates.isLoading}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue>
                    <span className="flex min-w-0 items-center gap-2">
                      {selectedLogo && (
                        <img
                          src={selectedLogo.imageUrl}
                          alt=""
                          className="size-5 shrink-0 object-contain"
                        />
                      )}
                      <span className="truncate">{logoLabel}</span>
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_LOGO}>Без логотипа</SelectItem>
                  {logoTemplates.logos.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      <img src={l.imageUrl} alt="" className="size-5 shrink-0 object-contain" />
                      {l.name}
                    </SelectItem>
                  ))}
                  <SelectItem value={LOGO_FILE}>Файл с компьютера…</SelectItem>
                </SelectContent>
              </Select>
              <TemplatesButton
                label="Шаблоны логотипов"
                onClick={() => setLogosOpen(true)}
                disabled={isGenerating}
              />
            </div>
            {logoId === LOGO_FILE && (
              <ImageField
                id="presentation-logo"
                label=""
                hint="Лучше PNG с прозрачным фоном"
                file={logo}
                onChange={setLogo}
                disabled={isGenerating}
              />
            )}
            {logoTemplates.error && (
              <FieldDescription className="text-destructive">
                {logoTemplates.error}
              </FieldDescription>
            )}
          </Field>

          <Field>
            <FieldLabel>Контакты</FieldLabel>
            <div className="flex gap-2">
              <Select
                value={contactId}
                onValueChange={(value) => setContactId(value ?? NO_CONTACT)}
                disabled={isGenerating || templates.isLoading}
              >
                <SelectTrigger className="flex-1">
                  <SelectValue>
                    <span className="truncate">{contactLabel}</span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT}>Без контактов</SelectItem>
                  {templates.contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                      {c.role ? ` — ${c.role}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <TemplatesButton
                label="Шаблоны контактов"
                onClick={() => setContactsOpen(true)}
                disabled={isGenerating}
              />
            </div>
            {templates.error && (
              <FieldDescription className="text-destructive">{templates.error}</FieldDescription>
            )}
          </Field>
          <Field>
            <FieldLabel htmlFor="presentation-year">
              Период
              {periodIsAuto && (
                <span className="text-muted-foreground font-normal">— по датам публикаций</span>
              )}
            </FieldLabel>
            <div className="flex gap-2">
              <Select
                value={month}
                onValueChange={(value) => setMonth(value ?? AUTO)}
                disabled={isGenerating}
              >
                <SelectTrigger className="flex-1 capitalize">
                  <SelectValue>{monthLabel}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>По датам публикаций</SelectItem>
                  {MONTHS_RU.map((name, i) => (
                    <SelectItem key={name} value={String(i)} className="capitalize">
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="presentation-year"
                className="w-24 shrink-0 tabular-nums"
                inputMode="numeric"
                placeholder={String(autoPeriod.year)}
                value={year}
                onChange={(e) => setYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                disabled={isGenerating}
                aria-label="Год на титуле"
                aria-invalid={!yearValid}
              />
            </div>
            {!yearValid && (
              <FieldDescription className="text-destructive">Год от 2000 до 2100</FieldDescription>
            )}
          </Field>

          <ImageField
            id="presentation-cover"
            label="Картинка на титуле"
            hint="Иначе — превью первой публикации"
            file={cover}
            onChange={setCover}
            disabled={isGenerating}
          />
        </div>
      </div>

      <div className="space-y-3">
        <SectionTitle
          action={
            <Button
              variant="ghost"
              size="sm"
              className="gap-1"
              disabled={isGenerating}
              onClick={() => {
                setArticles((prev) => [...prev, { key: nextKey, file: null, domain: '', url: '' }]);
                setNextKey((k) => k + 1);
              }}
            >
              <PlusIcon />
              Добавить
            </Button>
          }
        >
          Статьи
        </SectionTitle>
        {articles.map((a, i) => (
          <ArticleRow
            key={a.key}
            article={a}
            index={i}
            onChange={(patch) => patchArticle(a.key, patch)}
            onRemove={() => setArticles((prev) => prev.filter((x) => x.key !== a.key))}
            disabled={isGenerating}
          />
        ))}
        {!articlesValid && (
          <FieldDescription className="text-destructive">
            У каждой статьи нужны скриншот и домен
          </FieldDescription>
        )}
      </div>

      <ExportMenu
        rememberKey={EXPORT_KEY}
        label="Сформировать"
        formats={EXPORT_FORMATS}
        onSelect={(key) => handleGenerate(key as FormatKey)}
        disabled={!canGenerate}
        busy={isGenerating}
        busyLabel="Собираем презентацию…"
        className="w-full"
      />

      {busy && (progress || posts.progress) && (
        <p className="text-muted-foreground text-sm">{progress || posts.progress}</p>
      )}

      {posts.errors.length > 0 && (
        <div className="border-destructive/40 space-y-1 rounded-md border p-3">
          <p className="text-sm font-medium">Не попали в отчёт:</p>
          <ul className="space-y-1 text-xs">
            {posts.errors.map((f) => (
              <li key={f.url} className="break-all">
                <span className="text-muted-foreground">{f.url}</span> — {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <LogoTemplatesDialog
        open={logosOpen}
        onOpenChange={setLogosOpen}
        logos={logoTemplates.logos}
        onSave={logoTemplates.save}
        onRemove={async (id) => {
          await logoTemplates.remove(id);
          if (id === logoId) setLogoId(NO_LOGO);
        }}
        onSaved={(l) => setLogoId(l.id)}
      />

      <ContactTemplatesDialog
        open={contactsOpen}
        onOpenChange={setContactsOpen}
        contacts={templates.contacts}
        onSave={templates.save}
        onRemove={async (id) => {
          await templates.remove(id);
          if (id === contactId) setContactId(NO_CONTACT);
        }}
        onSaved={(c) => setContactId(c.id)}
      />
    </div>
  );
}
